/**
 * SN_SAI_PRC_REG_001 — 단가 관리 (본사) (2026-09-18, 2차 검토 확정)
 *
 * LLM 단가표(sai_ummodel) — LLM회사 · 모델(공통코드 SAI031) · 토큰종류 · 시작일 · 종료일 · 환종 · 단가 · 단가단위. 기간으로 이력을 쌓는다.
 * 행추가 시 시작일 오늘(훅) · 종료일 2100-12-31 · 환종 USD(006) · 단가단위 100만 토큰당(cd_value). 같은 회사 · 모델 · 토큰종류의 기간이 겹치면 서버가 거부한다.
 * 여기 단가가 있는 모델만 모델 배정 · 지정모델에 쓸 수 있다. 초기값은 12_AI_단가_초기등록.sql. 값은 LLM회사 공개 가격 페이지를 보고 사람이 넣는다.
 *
 * 조회조건 · 그리드 컬럼 · 폼 · 팝업 정의는 DB(sys_formctrl · sys_formgridcolumn · sys_popup*, 08_AI_화면설정.sql)에서 온다. 조회는 sys_query sn_sai_prc_reg_001_01.
 * 본 : 품목단가등록 SN_SAL_BAS_REG_001 (조회 + 그리드 1, 기간 관리)
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { dateToString } from "@shared/utils/DateUtils";
import { menuInfo } from "@shared/type/CommonType";

interface Props {
  Menuprops: menuInfo;
}

const SN_SAI_PRC_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
  });

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormData(rtn.formData);
        setFormControlData(rtn.formControlData);
        setColums({
          grid1: rtn.grid.grid1 || [],
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch, onInsert, onDelete, onSave,
  }));

  const onSearch = async () => {
    if (
      gridRefs.current["grid1"].current.getChange().modifiedRowcount > 0
    ) {
      const confirm = await showDialog("confirmCancel", "수정된 내용이 있습니다. 저장하시겠습니까?");
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) return;
      } else if (confirm === undefined) {
        return;
      }
    }
    try {
      /* 다시 읽기 전에 수정 표시를 지운다. 안 지우면 아니오를 눌러도 "수정 중" 으로 남는다 */
      gridRefs.current["grid1"].current?.resetUpdate?.();
      const params = {
        id: "sn_sai_prc_reg_001_01",
        params: searchRef.current.getFormdata(),
      };
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data?.[0] || [],
      });
    } catch (error: any) {}
  };

  const onInsert = async () => {
    try {
      gridRefs.current["grid1"].current?.addRow();
    } catch (error: any) {}
  };
  const onDelete = async () => {
    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) return;
    const confirm = await showDialog("confirm", "선택한 행을 삭제 표시합니다. 저장하면 지워집니다.");
    if (!confirm) return;
    gridRefs.current["grid1"].current?.deleteSelectedRows();
  };
  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current.stopEditing();
      if (!gridRefs.current["grid1"].current.requiredValuesCheck()) return false;
      const sai_ummodelChanges = gridRefs.current["grid1"].current.getChange();
      if (sai_ummodelChanges.modifiedRowcount === 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }
      const saveData: any = {
        sai_ummodel: sai_ummodelChanges,
      };
      const ok = await fnSave("SN_SAI_PRC_REG_001_SAVE_01", saveData);
      if (!ok) return false;
      gridRefs.current["grid1"].current.resetUpdate();
      await onSearch();
      return true;
    } catch (error: any) {
      gridRefs.current["grid1"].current?.resetUpdate(true);
      return false;
    }
  };
  /** 행추가 기본값 — 회사 · 조회조건의 회사 · 토큰종류 · 시작일 오늘 · 단가 0. 고정값(사용여부 등)은 sys_formgridcolumn.cd_value 가 넣는다 */
  const onBeforeAddPrice = (): any => {
    const cond = searchRef.current?.getFormdata?.() || {};
    return {
      cd_firm: userInfo.cd_firm, tp_provider: cond.tp_provider || "OPENAI", tp_token: cond.tp_token || "IN",
      dt_from: dateToString(new Date()), um_price: 0,
    };
  };
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onBeforeAddRow: onBeforeAddPrice
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel ref={searchRef} data={formData} controls={formControlData} />
      </div>
      <div className="w-full flex h-full min-h-0 space-x-1.5">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "단가" }}
            events={grids.grid1.events}
          />
      </div>
    </div>
  );
});

export default SN_SAI_PRC_REG_001;
